/**
 * Indian states/UTs and their cities, hardcoded for the registration form's
 * State → City cascade (13 Sep 2026, Parinaam's request).
 *
 * A hardcoded list rather than a reference table or a third-party API: the point
 * is to cut the free-text spelling variants ("Bangalore" / "Bengaluru" / "bengaluru")
 * that make a city un-countable, and the list of Indian cities barely changes. It
 * is NOT exhaustive — every state ends in an `OTHER_CITY` option that reveals a
 * free-text box, so a town that is not listed is never a dead end; it is just
 * typed in. City values are stored as-is in `volunteers.city` (a free-text column),
 * so no migration is needed.
 *
 * Kept alphabetical within each state so the dropdown reads in order.
 */

/** The sentinel option value that means "my city is not listed" → free text. */
export const OTHER_CITY = '__OTHER__';

/**
 * The states and union territories, in one alphabetical list (states and UTs
 * interleaved, not grouped) — that is the order the dropdown shows them.
 */
export const INDIA_STATES: readonly string[] = [
  'Andaman and Nicobar Islands',
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chandigarh',
  'Chhattisgarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu and Kashmir',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Ladakh',
  'Lakshadweep',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Puducherry',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
];

/**
 * Major cities per state/UT. Not every settlement — the well-known ones, with
 * `OTHER_CITY` as the catch-all. Extend a list here when a common city is missing.
 */
export const CITIES_BY_STATE: Record<string, readonly string[]> = {
  'Andhra Pradesh': [
    'Anantapur',
    'Chittoor',
    'Eluru',
    'Guntur',
    'Kadapa',
    'Kakinada',
    'Kurnool',
    'Nellore',
    'Ongole',
    'Rajahmundry',
    'Tirupati',
    'Vijayawada',
    'Visakhapatnam',
    'Vizianagaram',
  ],
  'Arunachal Pradesh': ['Itanagar', 'Naharlagun', 'Pasighat', 'Tawang', 'Ziro'],
  Assam: ['Dibrugarh', 'Dispur', 'Guwahati', 'Jorhat', 'Nagaon', 'Silchar', 'Tezpur', 'Tinsukia'],
  Bihar: [
    'Ara',
    'Begusarai',
    'Bhagalpur',
    'Bihar Sharif',
    'Darbhanga',
    'Gaya',
    'Katihar',
    'Muzaffarpur',
    'Patna',
    'Purnia',
  ],
  Chhattisgarh: ['Bhilai', 'Bilaspur', 'Durg', 'Korba', 'Raigarh', 'Raipur', 'Rajnandgaon'],
  Goa: ['Mapusa', 'Margao', 'Panaji', 'Ponda', 'Vasco da Gama'],
  Gujarat: [
    'Ahmedabad',
    'Anand',
    'Bharuch',
    'Bhavnagar',
    'Gandhinagar',
    'Jamnagar',
    'Junagadh',
    'Nadiad',
    'Rajkot',
    'Surat',
    'Vadodara',
    'Valsad',
    'Vapi',
  ],
  Haryana: [
    'Ambala',
    'Faridabad',
    'Gurugram',
    'Hisar',
    'Karnal',
    'Panipat',
    'Rohtak',
    'Sonipat',
    'Yamunanagar',
  ],
  'Himachal Pradesh': ['Dharamshala', 'Kullu', 'Mandi', 'Shimla', 'Solan', 'Una'],
  Jharkhand: ['Bokaro Steel City', 'Deoghar', 'Dhanbad', 'Hazaribagh', 'Jamshedpur', 'Ranchi'],
  Karnataka: [
    'Ballari',
    'Belagavi',
    'Bengaluru',
    'Bidar',
    'Davangere',
    'Dharwad',
    'Gulbarga (Kalaburagi)',
    'Hubballi',
    'Mangaluru',
    'Mysuru',
    'Shivamogga',
    'Tumakuru',
    'Udupi',
    'Vijayapura',
  ],
  Kerala: [
    'Alappuzha',
    'Kannur',
    'Kochi',
    'Kollam',
    'Kottayam',
    'Kozhikode',
    'Malappuram',
    'Palakkad',
    'Thiruvananthapuram',
    'Thrissur',
  ],
  'Madhya Pradesh': ['Bhopal', 'Gwalior', 'Indore', 'Jabalpur', 'Rewa', 'Sagar', 'Satna', 'Ujjain'],
  Maharashtra: [
    'Ahmednagar',
    'Akola',
    'Amravati',
    'Aurangabad (Chhatrapati Sambhajinagar)',
    'Jalgaon',
    'Kolhapur',
    'Latur',
    'Mumbai',
    'Nagpur',
    'Nanded',
    'Nashik',
    'Navi Mumbai',
    'Pune',
    'Sangli',
    'Solapur',
    'Thane',
  ],
  Manipur: ['Imphal', 'Thoubal'],
  Meghalaya: ['Shillong', 'Tura'],
  Mizoram: ['Aizawl', 'Lunglei'],
  Nagaland: ['Dimapur', 'Kohima'],
  Odisha: ['Balasore', 'Berhampur', 'Bhubaneswar', 'Cuttack', 'Puri', 'Rourkela', 'Sambalpur'],
  Punjab: [
    'Amritsar',
    'Bathinda',
    'Hoshiarpur',
    'Jalandhar',
    'Ludhiana',
    'Mohali',
    'Pathankot',
    'Patiala',
  ],
  Rajasthan: [
    'Ajmer',
    'Alwar',
    'Bhilwara',
    'Bikaner',
    'Jaipur',
    'Jodhpur',
    'Kota',
    'Sikar',
    'Udaipur',
  ],
  Sikkim: ['Gangtok', 'Namchi'],
  'Tamil Nadu': [
    'Chennai',
    'Coimbatore',
    'Erode',
    'Madurai',
    'Nagercoil',
    'Salem',
    'Thanjavur',
    'Tiruchirappalli',
    'Tirunelveli',
    'Tiruppur',
    'Vellore',
  ],
  Telangana: [
    'Hyderabad',
    'Karimnagar',
    'Khammam',
    'Mahbubnagar',
    'Nizamabad',
    'Secunderabad',
    'Warangal',
  ],
  Tripura: ['Agartala', 'Dharmanagar', 'Udaipur'],
  'Uttar Pradesh': [
    'Agra',
    'Aligarh',
    'Prayagraj (Allahabad)',
    'Bareilly',
    'Ghaziabad',
    'Gorakhpur',
    'Kanpur',
    'Lucknow',
    'Meerut',
    'Moradabad',
    'Noida',
    'Saharanpur',
    'Varanasi',
  ],
  Uttarakhand: ['Dehradun', 'Haldwani', 'Haridwar', 'Nainital', 'Roorkee', 'Rudrapur'],
  'West Bengal': ['Asansol', 'Bardhaman', 'Durgapur', 'Howrah', 'Kharagpur', 'Kolkata', 'Siliguri'],
  'Andaman and Nicobar Islands': ['Port Blair'],
  Chandigarh: ['Chandigarh'],
  'Dadra and Nagar Haveli and Daman and Diu': ['Daman', 'Diu', 'Silvassa'],
  Delhi: ['Delhi', 'New Delhi'],
  'Jammu and Kashmir': ['Anantnag', 'Baramulla', 'Jammu', 'Srinagar'],
  Ladakh: ['Kargil', 'Leh'],
  Lakshadweep: ['Kavaratti'],
  Puducherry: ['Karaikal', 'Puducherry'],
};

/** The cities to offer for a state — always ending in the "Other" escape hatch. */
export function citiesForState(state: string): readonly string[] {
  return CITIES_BY_STATE[state] ?? [];
}
